import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Skeleton, Tooltip, Typography } from '@mui/material';
import { CircleCheck, CircleDashed, Users } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useGetEmployeesQuery, Employee } from '../../store/slices/employeeSlice';
import { useGetDepartmentsQuery } from '../../store/slices/departmentSlice';
import { useGetApprovalConfigQuery, useGetProfilesQuery, useSyncManagerLinksMutation, Profile } from '../../store/slices/adminSlice';
import { openModal } from '../../store/slices/uiSlice';
import {
  EmptyState,
  ErrorState,
  FilterChip,
  KpiGrid,
  KpiTile,
  SearchField,
  SegmentedControl,
  StatusPill,
  useDelayedFlag,
} from '../../components/ui';
import type { SegmentedOption, StatusTone } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';
import { formatCount, formatDate, formatDateTime, formatNumber, formatPercent, formatTime, MISSING } from '../../utils/format';
import IssueGroups, { SyncButton } from './setup-health-parts/IssueGroups';
import type { FixActions } from './setup-health-parts/IssueGroups';
import EmployeeTable from './setup-health-parts/EmployeeTable';
import {
  APPROVAL_CARD_ID,
  ApprovalRoutingCard,
  CheckAgainButton,
  CheckAreaChips,
  ChecksCard,
} from './setup-health-parts/SideCards';
import type { Coverage } from './setup-health-parts/SideCards';
import { areaHealth, areaLabel, groupByIssue, guideFor, hasSyncFix, inArea, matchesSearch } from './setup-health-parts/issueGuide';
import type { CheckAreaKey } from './setup-health-parts/issueGuide';
import { ACCESS_TAB_LABEL } from '../../utils/settingsTabs';

export type IssueSeverity = 'critical' | 'warning';

export interface SetupIssue {
  key: string;
  severity: IssueSeverity;
  label: string;
  detail: string;
}

export interface AuditedEmployee {
  employee: Employee;
  issues: SetupIssue[];
  hasCritical: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirrors normalizeName in backend adminController.syncManagerLinks so the
// "would sync fix this" verdict matches what sync actually does.
const normalizeName = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

// Mirrors the manager-dashboard fallback comparison (trim + lowercase).
const softName = (value: string) => value.trim().toLowerCase();

// Today in the Eastern calendar (matches the backend tenure policy's
// todayEasternYmd). Assembled from formatToParts because format()'s part
// ORDER is locale-data dependent — a non-ISO string would silently break
// the lexicographic date compare in the audit.
const easternTodayYmd = () => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
};

const isActive = (employee: Employee) =>
  employee.employmentStatus === 'active' || !employee.employmentStatus;

export function auditEmployees(
  employees: Employee[],
  profiles: Profile[],
  profilesLoaded: boolean,
  // Departments drive the approval-queue fallback: pending lists match
  // employee.managerUserId first, else the employee's department manager.
  // Pass null while departments are loading/unavailable so queue checks are
  // deferred instead of fabricating criticals from missing data.
  departments: Array<{ _id: string; managerUserId?: string | null; isSalesTeam?: boolean }> | null = null,
  // Injectable for tests; future-dated hires intentionally carry zero
  // allotments until their start date, so allotment checks skip them.
  todayYmd: string = easternTodayYmd()
): AuditedEmployee[] {
  const activeEmployees = employees.filter(isActive);

  const profileById = new Map(profiles.map((p) => [p.id, p]));
  const profileByEmail = new Map(
    profiles.filter((p) => p.email).map((p) => [p.email.toLowerCase(), p])
  );
  const profileNameCounts = new Map<string, number>();
  profiles.forEach((p) => {
    if (!p.fullName) return;
    const key = normalizeName(String(p.fullName));
    profileNameCounts.set(key, (profileNameCounts.get(key) || 0) + 1);
  });
  const profilesByLastName = new Map<string, Array<{ id: string; first: string }>>();
  profiles.forEach((p) => {
    if (!p.fullName) return;
    const parts = normalizeName(String(p.fullName)).split(' ');
    if (parts.length < 2) return;
    const last = parts[parts.length - 1];
    if (!profilesByLastName.has(last)) profilesByLastName.set(last, []);
    profilesByLastName.get(last)!.push({ id: p.id, first: parts[0] });
  });

  // Same heuristic as backend resolveManagerId: exact normalized name, then
  // unique last-name + first-name-prefix match. The backend's name map is
  // last-write-wins, so when two login accounts share a normalized name, sync
  // would link an ARBITRARY one — surface that as ambiguous instead of
  // recommending sync.
  const resolveProfileByName = (name: string): { matched: boolean; ambiguous: boolean } => {
    const normalized = normalizeName(name);
    const directCount = profileNameCounts.get(normalized) || 0;
    if (directCount > 0) {
      return { matched: true, ambiguous: directCount > 1 };
    }
    const parts = normalized.split(' ');
    if (parts.length < 2) return { matched: false, ambiguous: false };
    const candidates = profilesByLastName.get(parts[parts.length - 1]) || [];
    // The backend fallback refuses non-unique matches, so >1 here means sync
    // simply won't link anything (not ambiguous-dangerous, just unmatched).
    return { matched: candidates.filter((c) => c.first.startsWith(parts[0])).length === 1, ambiguous: false };
  };

  const employeesBySoftName = new Map<string, Employee[]>();
  const employeeByUserId = new Map<string, Employee>();
  employees.forEach((e) => {
    if (e.name) {
      const key = softName(e.name);
      const existing = employeesBySoftName.get(key);
      if (existing) {
        existing.push(e);
      } else {
        employeesBySoftName.set(key, [e]);
      }
    }
    if (e.userId) employeeByUserId.set(e.userId, e);
  });

  const deptManagerByDeptId = new Map<string, string>();
  const salesDepartmentIds = new Set<string>();
  (departments || []).forEach((d) => {
    if (d.managerUserId) deptManagerByDeptId.set(d._id, d.managerUserId);
    if (d.isSalesTeam) salesDepartmentIds.add(d._id);
  });

  // Mirrors the backend name resolvers (resolveManagerEmail / resolveVpEmail),
  // which do an exact `.eq('name', x).maybeSingle()` lookup with NO trimming
  // or case folding: duplicates of the exact same name make that lookup error
  // out, and any case OR whitespace drift (`exact: false`) makes it miss
  // entirely — even though the manager dashboard's softer trim+lowercase
  // matching still works. Callers must pass the RAW stored value, not a
  // trimmed copy, so padding counts as drift exactly like it does in prod.
  const resolveEmployeeByName = (rawName: string) => {
    const matches = employeesBySoftName.get(softName(rawName)) || [];
    const exactMatches = matches.filter((m) => (m.name || '') === rawName);
    return {
      matched: matches.length > 0,
      exact: exactMatches.length >= 1,
      ambiguous: exactMatches.length > 1,
      match: exactMatches[0] || matches[0],
    };
  };

  const emailCounts = new Map<string, number>();
  const userIdCounts = new Map<string, number>();
  activeEmployees.forEach((e) => {
    // Trim to match the per-employee lookup below, so whitespace-padded
    // legacy emails still count as duplicates of their clean twin.
    const email = (e.email || '').trim().toLowerCase();
    if (email) emailCounts.set(email, (emailCounts.get(email) || 0) + 1);
    if (e.userId) userIdCounts.set(e.userId, (userIdCounts.get(e.userId) || 0) + 1);
  });

  return activeEmployees.map((employee) => {
    const issues: SetupIssue[] = [];
    const push = (key: string, severity: IssueSeverity, label: string, detail: string) =>
      issues.push({ key, severity, label, detail });

    const name = (employee.name || '').trim();
    const email = (employee.email || '').trim();
    const managerName = (employee.managerName || '').trim();
    const managerUserId = employee.managerUserId || null;
    const jrAeUserId = employee.jrAeUserId || null;

    // ── Profile basics ──
    if (!name) {
      push('no-name', 'critical', 'No name', 'The employee record has no name. Nothing that matches by name (manager links, VP escalation) can work.');
    }
    if (!email) {
      push('no-email', 'critical', 'No email', 'Approval results, reminders, and summaries have nowhere to go.');
    } else if (!EMAIL_RE.test(email)) {
      push('bad-email', 'critical', 'Invalid email', `"${email}" doesn't look like a valid address, so notification emails will bounce.`);
    } else if ((emailCounts.get(email.toLowerCase()) || 0) > 1) {
      push('dup-email', 'warning', 'Duplicate email', 'Another active employee record uses this same email. Time off and logins can attach to the wrong record.');
    }
    if (!(employee.role || '').trim()) {
      push('no-role', 'warning', 'No role', 'Role is blank. It is required by the employee form and shows as a blank chip in the directory.');
    }
    if (!employee.department?._id) {
      push('no-department', 'critical', 'No department', 'Not assigned to a department, so they are missing from department views, coverage counts, and reporting.');
    }
    if (!(employee.teamName || '').trim()) {
      push('no-team', 'warning', 'No team', 'Team is blank, so team-based views and weekend shift grouping skip this person.');
    }

    // ── Manager assignment (drives whose dashboard their time off shows on) ──
    const managerProfile = managerUserId ? profileById.get(managerUserId) : undefined;

    if (!managerName && !managerUserId) {
      push('no-manager', 'critical', 'No manager assigned', 'Manager name and manager account are both blank. Time-off requests will not appear on any manager dashboard and approval emails are skipped.');
    } else {
      if (managerUserId && profilesLoaded && !managerProfile) {
        push('manager-link-broken', 'critical', 'Manager link broken', 'The linked manager account no longer exists. Re-select the manager in Edit.');
      }
      if (managerUserId && managerProfile && managerProfile.role !== 'admin' && managerProfile.role !== 'hr' && managerProfile.role !== 'manager') {
        push('manager-no-role', 'warning', 'Manager lacks manager access', `The linked manager account (${managerProfile.fullName || managerProfile.email}) has the "${managerProfile.role || 'no'}" role, so they can approve from emails but cannot open the manager dashboard or time-off pages. Grant them the Manager role in ${ACCESS_TAB_LABEL}.`);
      }
      if (!managerUserId && managerName) {
        // Resolve with the raw stored value: backend lookups don't trim.
        const managerByName = resolveEmployeeByName(employee.managerName || '');
        if (managerByName.ambiguous) {
          // The backend approval-email lookup is an exact-name maybeSingle();
          // duplicate names make it error out, so no manager email is sent.
          push('manager-ambiguous', 'critical', 'Manager name is ambiguous', `Multiple employees are named "${managerName}", so the approval-email lookup fails and no manager email is sent. Pick the manager account in Edit to disambiguate.`);
        } else if (managerByName.matched && !managerByName.exact) {
          // Soft match only (case/spacing drift): the Manager Dashboard's
          // trim+lowercase matching still shows the team, but the backend
          // approval-email lookup is exact and silently sends nothing.
          push('manager-name-inexact', 'critical', 'Manager name spelling is off', `"${managerName}" only loosely matches ${managerByName.match?.name || 'an employee'} (case/spacing differ). Approval emails use an exact-name lookup, so no manager email is sent — requests only surface if the manager checks their dashboard. Fix the spelling or pick the manager account in Edit.`);
        } else if (managerByName.matched) {
          // Exact name match: approval emails send and the Manager Dashboard
          // (which matches by name and can approve/decline) works. The Time
          // Off / HR approval pages, however, route only by linked account or
          // department manager — check that fallback for full coverage.
          const deptId = employee.department?._id;
          const deptFallbackManagerId = deptId ? deptManagerByDeptId.get(deptId) : undefined;
          const queueCovered = Boolean(
            managerByName.match?.userId &&
            deptFallbackManagerId &&
            deptFallbackManagerId === managerByName.match.userId
          );
          if (departments && !queueCovered) {
            push('manager-queue-gap', 'warning', 'Missing from approval pages', `Matched by name only ("${managerName}"). Emails and the manager dashboard work, but the time-off approval pages route only by linked account or department manager — neither points at them there. Run "Sync manager links" or pick the manager account in Edit.`);
          } else {
            push('manager-name-only', 'warning', 'Manager not linked', `Matched by name only ("${managerName}"). This works while names stay identical, but breaks silently if they drift. Run "Sync manager links" or pick the manager account in Edit.`);
          }
        } else if (profilesLoaded) {
          // A name that resolves only to a login profile (no employee record)
          // cannot route anything today: approval emails fall back to an
          // employees-table name lookup and the manager dashboard fallback
          // compares employee names. Sync writes the missing link, so this is
          // broken-but-one-click-fixable — unless the profile name is
          // duplicated, in which case sync would link an arbitrary account.
          const profileMatch = resolveProfileByName(managerName);
          if (profileMatch.ambiguous) {
            push('manager-profile-ambiguous', 'critical', 'Multiple accounts share this name', `"${managerName}" matches more than one login account and no employee record. Running "Sync manager links" would link an arbitrary account — pick the correct manager account in Edit instead.`);
          } else if (profileMatch.matched) {
            push('manager-not-synced', 'critical', 'Manager not linked to routing', `"${managerName}" matches a login account but no employee record is linked, so time off won't reach them until the link is written. Run "Sync manager links" to fix this.`);
          } else {
            push('manager-unrecognized', 'critical', 'Manager not recognized', `"${managerName}" doesn't match any employee or user account, so their time off won't reach a manager.`);
          }
        }
        // While profiles are still loading (or unavailable) we can't tell a
        // sync-fixable profile match from a typo, so defer judgment instead of
        // flashing false criticals. The audit re-runs when profiles settle.
      }
      if (
        managerUserId &&
        managerProfile?.fullName &&
        managerName &&
        normalizeName(managerName) !== normalizeName(String(managerProfile.fullName))
      ) {
        push('manager-mismatch', 'warning', 'Manager name mismatch', `Manager name says "${managerName}" but the linked account is ${managerProfile.fullName}. One of the two is wrong — this is how time off ends up with the wrong manager.`);
      }
      if (managerUserId && managerProfile && !managerName) {
        push('manager-name-blank', 'warning', 'Manager name blank', 'A manager account is linked but the manager name field is empty, so the directory and name-based matching show nothing.');
      }
      if (managerName.includes('@')) {
        push('manager-name-email', 'warning', 'Manager name is an email', `Manager name holds "${managerName}". Use the manager's full name here — email-style values break name matching on the manager dashboard.`);
      }

      const managerEmployee =
        (managerUserId ? employeeByUserId.get(managerUserId) : undefined) ||
        (managerName ? resolveEmployeeByName(employee.managerName || '').match : undefined);
      if (managerEmployee && !isActive(managerEmployee)) {
        push('manager-inactive', 'critical', 'Manager no longer active', `Their manager (${managerEmployee.name}) is marked ${managerEmployee.employmentStatus?.replace(/_/g, ' ')}. Requests route to someone who left — reassign a manager.`);
      }

      const selfByUser = Boolean(managerUserId && employee.userId && managerUserId === employee.userId);
      const selfByName = Boolean(managerName && name && softName(managerName) === softName(name));
      if (selfByUser || selfByName) {
        push('self-managed', 'warning', 'Self-managed', 'This employee is listed as their own manager, so they would approve their own time off.');
      }
    }

    // ── Optional Jr manager assignment (must point to a Jr manager login) ──
    if (jrAeUserId) {
      const jrAeProfile = profileById.get(jrAeUserId);
      if (profilesLoaded && !jrAeProfile) {
        push('jr-ae-link-broken', 'critical', 'Jr manager link broken', 'The assigned Jr manager account no longer exists. Re-select the Jr manager in Edit.');
      } else if (jrAeProfile && jrAeProfile.role !== 'jr_ae') {
        push('jr-ae-wrong-role', 'critical', 'Assigned user is not a Jr manager', `${jrAeProfile.fullName || jrAeProfile.email} has the "${jrAeProfile.role || 'no'}" role, so scoped pages will not open for them. Grant the Jr manager role or clear this assignment.`);
      }

      const jrAeEmployee = employeeByUserId.get(jrAeUserId);
      if (jrAeEmployee && !isActive(jrAeEmployee)) {
        push('jr-ae-inactive', 'critical', 'Jr manager no longer active', `The assigned Jr manager (${jrAeEmployee.name}) is marked ${jrAeEmployee.employmentStatus?.replace(/_/g, ' ')}. Reassign or clear the Jr manager.`);
      }
      if (employee.userId && jrAeUserId === employee.userId) {
        push('self-jr-ae', 'warning', 'Self-assigned Jr manager', 'This employee is assigned as their own Jr manager and could approve their own time off.');
      }
    }

    // ── Auth user link (drives their own login and dashboard) ──
    if (!employee.userId) {
      const matchingProfile = profilesLoaded && email ? profileByEmail.get(email.toLowerCase()) : undefined;
      if (matchingProfile) {
        push('auth-unlinked', 'warning', 'Login not linked', `A login exists for ${email} but isn't linked to this record. Run "Sync manager links" to connect it.`);
      } else {
        push('no-auth-user', 'warning', 'No login linked', 'No auth user is linked, so this person cannot sign in to submit time off or see balances.');
      }
    } else if (profilesLoaded && !profileById.get(employee.userId)) {
      push('auth-link-broken', 'critical', 'Login link broken', 'The linked auth account no longer exists. Re-select the employee auth user in Edit.');
    } else if ((userIdCounts.get(employee.userId) || 0) > 1) {
      push('auth-shared', 'critical', 'Login shared', 'Another active employee record is linked to the same login. Time off can attach to the wrong record.');
    }

    // ── Time-off allotments ──
    // Future-dated POLICY-MANAGED hires deliberately hold zero allotments
    // until their start date (the tenure policy grants them that morning), so
    // zero is healthy. Custom-allotment hires get nothing from the policy —
    // a 0/0 custom hire is a data-entry mistake worth surfacing before day 1.
    const startDateValue = (employee.startDate || '').trim();
    const startsInFuture = Boolean(
      startDateValue && startDateValue > todayYmd && !employee.customAllotment
    );
    const pto = employee.ptoAllotment || 0;
    const sick = employee.sickTimeAllotment || 0;
    if (!startsInFuture) {
      if (pto <= 0 && sick <= 0) {
        push('no-allotments', 'critical', 'No time-off allotments', 'PTO and sick are both 0, so balances show empty and any request draws from nothing. Set the allotments in Edit.');
      } else {
        if (pto <= 0) push('no-pto', 'warning', 'PTO allotment is 0', 'PTO allotment is 0 days. If that is not intentional, their PTO balance shows empty.');
        if (sick <= 0) push('no-sick', 'warning', 'Sick allotment is 0', 'Sick allotment is 0 days. If that is not intentional, their sick balance shows empty.');
      }
    }
    if (!startDateValue && !employee.customAllotment) {
      push('no-start-date', 'warning', 'No start date', 'The tenure policy cannot calculate PTO for this employee, so their allotments are frozen at current values. Set the start date in Edit.');
    }

    // ── WFH escalation ──
    const isSalesEmployee = Boolean(
      employee.department?._id
      && salesDepartmentIds.has(employee.department._id)
    );
    // Sales requests use the configured email pool, not per-employee vpName.
    // The pool itself is audited once at page level below.
    if (departments && !isSalesEmployee) {
    const vpName = (employee.vpName || '').trim();
    if (!employee.wfhEligible && !vpName) {
      push('no-vp', 'warning', 'No VP for WFH escalation', 'Not on a hybrid schedule, so WFH requests need VP approval — with no VP set they fall back to the default escalation address.');
    }
    if (vpName) {
      // Resolve with the raw stored value: backend lookups don't trim.
      const vpByName = resolveEmployeeByName(employee.vpName || '');
      if (!vpByName.matched) {
        push('vp-unrecognized', 'warning', 'VP not recognized', `VP "${vpName}" doesn't match any employee, so WFH escalation emails fall back to the default address.`);
      } else if (vpByName.ambiguous) {
        // Exact-name duplicates make the backend maybeSingle() lookup error,
        // which falls back to the default escalation address — same outcome
        // as unrecognized, but the fix is different (disambiguate the people).
        push('vp-ambiguous', 'warning', 'VP name is ambiguous', `Multiple employees are named "${vpName}", so WFH escalation can't resolve the intended VP and falls back to the default address.`);
      } else if (!vpByName.exact) {
        // Soft-only match: the backend VP lookup is exact, so escalation
        // misses this person and falls back to the default address.
        push('vp-name-inexact', 'warning', 'VP name spelling is off', `VP "${vpName}" only loosely matches ${vpByName.match?.name || 'an employee'} (case/spacing differ). The escalation lookup is exact, so WFH escalations fall back to the default address. Fix the spelling to match.`);
      } else if (vpByName.match && !isActive(vpByName.match)) {
        push('vp-inactive', 'warning', 'VP no longer active', `VP "${vpByName.match.name}" is marked ${vpByName.match.employmentStatus?.replace(/_/g, ' ')}. WFH escalation now uses the configured fallback recipient; assign a current VP to restore direct routing.`);
      }
    }
    }

    issues.sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1));

    return {
      employee,
      issues,
      hasCritical: issues.some((i) => i.severity === 'critical'),
    };
  });
}

export function useEmployeeSetupAudit() {
  const {
    data: employees = [],
    isLoading: employeesLoading,
    isFetching: employeesFetching,
    isError: employeesError,
    refetch: refetchEmployees,
    fulfilledTimeStamp: checkedAt,
  } = useGetEmployeesQuery();
  const {
    data: profiles = [],
    isSuccess: profilesLoaded,
    isLoading: profilesLoading,
    isFetching: profilesFetching,
    isError: profilesError,
    refetch: refetchProfiles,
  } = useGetProfilesQuery();
  const {
    data: departments,
    isLoading: departmentsLoading,
    isFetching: departmentsFetching,
    isError: departmentsError,
    refetch: refetchDepartments,
  } = useGetDepartmentsQuery();
  const {
    data: approvalConfig,
    isLoading: approvalConfigLoading,
    isFetching: approvalConfigFetching,
    isError: approvalConfigError,
    refetch: refetchApprovalConfig,
  } = useGetApprovalConfigQuery();

  // Kept outside the memo so loading-flag flips (e.g. a refetch starting)
  // don't force a recompute of the audit when the data itself is unchanged.
  const isLoading = employeesLoading || profilesLoading || departmentsLoading || approvalConfigLoading;

  const audit = useMemo(() => {
    const audited = auditEmployees(employees, profiles, profilesLoaded, departments ?? null);
    const flagged = audited
      .filter((a) => a.issues.length > 0)
      .sort((a, b) => {
        if (a.hasCritical !== b.hasCritical) return a.hasCritical ? -1 : 1;
        if (a.issues.length !== b.issues.length) return b.issues.length - a.issues.length;
        return (a.employee.name || '').localeCompare(b.employee.name || '');
      });
    return {
      activeCount: audited.length,
      flagged,
      criticalCount: flagged.filter((f) => f.hasCritical).length,
      warningCount: flagged.filter((f) => !f.hasCritical).length,
      healthyCount: audited.length - flagged.length,
    };
  }, [employees, profiles, profilesLoaded, departments]);

  return {
    ...audit,
    isLoading,
    employeesError,
    profilesError,
    departmentsError,
    approvalConfig,
    approvalConfigError,
    refetchEmployees,
    // Additive, presentation-only fields for the Setup health view: retry
    // for each source, and when the audit last ran.
    approvalConfigLoading,
    refetchProfiles,
    refetchDepartments,
    refetchApprovalConfig,
    checkedAt,
    isRefreshing: !isLoading && (employeesFetching || profilesFetching || departmentsFetching),
    // Busy states for "Retry checks" and "Check again" (refetches after the
    // first load, which isLoading doesn't cover).
    checksFetching: profilesFetching || departmentsFetching,
    approvalConfigFetching,
  };
}

/* ------------------------------------------------------------------------ */
/* View                                                                     */
/* ------------------------------------------------------------------------ */

type SeverityFilter = 'all' | 'critical' | 'warning';
type ViewMode = 'issue' | 'employee';

const VIEW_OPTIONS: SegmentedOption<ViewMode>[] = [
  { value: 'issue', label: 'By issue' },
  { value: 'employee', label: 'By employee' },
];

const formatCheckedAt = (timestamp: number) =>
  formatDate(timestamp) === formatDate(Date.now()) ? formatTime(timestamp) : formatDateTime(timestamp);

/**
 * Whole percent, rounded down, so one flagged record among hundreds never
 * reads as 100%. Integer math first: 29 * 100 / 100 is exactly 29, while
 * 0.29 * 100 is 28.999….
 */
const floorPercent = (part: number, whole: number) => formatPercent(Math.floor((part * 100) / whole));

/** Scrolls to the Sales WFH approvals card and moves focus to its title. */
const showApprovalCard = () => {
  const card = document.getElementById(APPROVAL_CARD_ID);
  if (!card) return;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  card.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  document.getElementById(`${APPROVAL_CARD_ID}-title`)?.focus({ preventScroll: true });
};

/** A plain surface for whole-region states (empty, error) on the canvas. */
const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ bgcolor: sbg.bg.surface, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.card }}>
    {children}
  </Box>
);

/** The issue list at its real shape, revealed after 300ms so fast loads never flash. */
const IssuesSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true);
  return (
    <Box role="status" aria-label="Checking employee setup" sx={{ display: 'grid', gap: 1, visibility: visible ? 'visible' : 'hidden' }}>
      <Skeleton variant="rounded" width={240} height={32} sx={{ borderRadius: sbg.radius.pill, mb: 1 }} />
      {[0, 1, 2, 3, 4].map((n) => (
        <Skeleton key={n} variant="rounded" height={68} sx={{ borderRadius: sbg.radius.lg }} />
      ))}
    </Box>
  );
};

/** Scope · freshness, joined with middle dots like the page header's meta line. */
const MetaLine: React.FC<{ parts: React.ReactNode[] }> = ({ parts }) => {
  const shown = parts.filter((part) => part !== null && part !== undefined && part !== false && part !== '');
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{ color: sbg.text.secondary, mt: 1, display: 'flex', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25, fontVariantNumeric: 'tabular-nums' }}
    >
      {shown.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Box component="span" aria-hidden="true" sx={{ color: sbg.border.strong }}>
              ·
            </Box>
          )}
          <span>{part}</span>
        </React.Fragment>
      ))}
    </Typography>
  );
};

const SetupHealth: React.FC = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [syncManagerLinks, { isLoading: isSyncing }] = useSyncManagerLinksMutation();
  const [syncMessage, setSyncMessage] = useState<{ severity: 'success' | 'warning' | 'error'; title: string; text: string } | null>(null);
  const [filter, setFilter] = useState<SeverityFilter>('all');
  const [view, setView] = useState<ViewMode>('issue');
  const [search, setSearch] = useState('');
  const [area, setArea] = useState<CheckAreaKey | null>(null);

  const {
    isLoading,
    activeCount,
    flagged,
    criticalCount,
    warningCount,
    healthyCount,
    employeesError,
    profilesError,
    departmentsError,
    approvalConfig,
    approvalConfigError,
    approvalConfigLoading,
    approvalConfigFetching,
    refetchEmployees,
    refetchProfiles,
    refetchDepartments,
    refetchApprovalConfig,
    checkedAt,
    isRefreshing,
    checksFetching,
  } = useEmployeeSetupAudit();

  // Profiles gate the manager-link/login checks; departments gate the
  // approval-queue and WFH escalation checks. Either failing means the audit
  // ran incomplete.
  const auditIncomplete = profilesError || departmentsError;
  const skippedReasons = [
    ...(profilesError ? ['Login accounts didn’t load, so manager-link and login checks were skipped.'] : []),
    ...(departmentsError ? ['Departments didn’t load, so approval-page and WFH escalation checks were skipped.'] : []),
  ];
  const partly = (reason: string): Coverage => ({ state: 'partial', reason });
  const coverage: Record<CheckAreaKey, Coverage> = {
    records: { state: 'full' },
    manager: profilesError
      ? partly('login accounts didn’t load')
      : departmentsError
        ? partly('departments didn’t load')
        : { state: 'full' },
    'jr-ae': profilesError ? partly('login accounts didn’t load') : { state: 'full' },
    login: profilesError ? partly('login accounts didn’t load') : { state: 'full' },
    allotments: { state: 'full' },
    wfh: departmentsError ? { state: 'none', reason: 'departments didn’t load' } : { state: 'full' },
  };

  // Missing or broken server settings. WFH emails being turned off is a
  // deliberate setting, not missing config; the card shows it on its own.
  const approvalConfigProblems = [
    approvalConfig?.salesVpError,
    approvalConfig?.escalationError,
    approvalConfig && !approvalConfig.backendPublicUrlConfigured
      ? 'BACKEND_PUBLIC_URL is not configured.'
      : null,
    approvalConfig && !approvalConfig.actionSecretConfigured
      ? 'WFH_ACTION_SECRET is not configured.'
      : null,
  ].filter((value): value is string => Boolean(value));

  // Degraded-but-working states: a configured reviewer address that no longer
  // resolves (the pool still has two reviewers), or no one outside the pool
  // being copied on missed sales deadlines.
  const approvalConfigWarnings = [
    approvalConfig && (approvalConfig.unresolvedSalesVpEmails || []).length > 0
      ? `These sales reviewer addresses don’t match an active employee and are skipped: ${approvalConfig.unresolvedSalesVpEmails.join(', ')}.`
      : null,
    approvalConfig?.escalationWarning,
  ].filter((value): value is string => Boolean(value));

  // Anything that changes the overall status moves the approvals card above
  // the issue list when the side column stacks.
  const configNeedsAttention =
    !approvalConfigLoading && (approvalConfigError || approvalConfigProblems.length > 0 || approvalConfigWarnings.length > 0);

  // The access tab is a sibling Settings tab; keep unrelated params.
  const accessSearch = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'access');
    return `?${next.toString()}`;
  }, [searchParams]);

  // Presentation-only narrowing: search and the area picked in Checks.
  const matching = useMemo(
    () => flagged.filter((item) => matchesSearch(item.employee, search) && inArea(item, area)),
    [flagged, search, area]
  );
  const employeeRows = matching.filter((f) =>
    filter === 'all' ? true : filter === 'critical' ? f.hasCritical : !f.hasCritical
  );
  const issueGroups = useMemo(
    () =>
      groupByIssue(
        area
          ? matching.map((item) => ({ ...item, issues: item.issues.filter((issue) => guideFor(issue).area === area) }))
          : matching
      ),
    [matching, area]
  );
  const health = useMemo(() => areaHealth(flagged), [flagged]);
  const syncFixes = useMemo(() => hasSyncFix(flagged), [flagged]);

  const query = search.trim();
  const filtersActive = query !== '' || area !== null || (view === 'employee' && filter !== 'all');
  const clearFilters = () => {
    setSearch('');
    setArea(null);
    setFilter('all');
  };

  const handleSync = async () => {
    setSyncMessage(null);
    try {
      const result = await syncManagerLinks().unwrap();
      const updated = result.employeeManagerUpdates + result.departmentManagerUpdates + result.employeeUserUpdates;
      const summary =
        updated === 0
          ? 'Every manager and login link was already up to date, so nothing changed.'
          : `Updated ${formatCount(result.employeeManagerUpdates, 'employee manager')}, ${formatCount(result.departmentManagerUpdates, 'department manager')} and ${formatCount(result.employeeUserUpdates, 'login link')}. The checks below update on their own.`;
      const warnings = result.warnings || [];
      setSyncMessage(
        warnings.length > 0
          ? { severity: 'warning', title: 'Manager links synced with warnings', text: `${summary} ${warnings.join(' ')}` }
          : { severity: 'success', title: 'Manager links synced', text: summary }
      );
      // Cache refresh happens via the mutation's invalidatesTags (employees AND
      // departments — sync updates department manager links too).
    } catch (err: any) {
      setSyncMessage({
        severity: 'error',
        title: 'Couldn’t sync manager links',
        text: err?.data?.error || err?.message || 'The server didn’t respond. Try again in a minute.',
      });
    }
  };

  const handleFix = (employee: Employee) => {
    dispatch(
      openModal({
        type: 'employee-form',
        data: { employee, isEdit: true },
      })
    );
  };

  const retryChecks = () => {
    if (profilesError) refetchProfiles();
    if (departmentsError) refetchDepartments();
  };

  const fixActions: FixActions = { onEdit: handleFix, onSync: handleSync, isSyncing, accessSearch };

  // Employee setup and the Sales WFH approval settings both feed the one
  // overall status, so the header never says "Healthy" beside a card that
  // says "Not configured".
  const overall: { tone: StatusTone; label: string } | null = isLoading
    ? null
    : employeesError
      ? { tone: 'danger', label: 'Check failed' }
      : criticalCount > 0
        ? { tone: 'danger', label: 'Needs setup' }
        : approvalConfigProblems.length > 0
          ? { tone: 'danger', label: 'Needs attention' }
          : flagged.length > 0
            ? { tone: 'warning', label: 'Needs setup' }
            : auditIncomplete || approvalConfigError
              ? { tone: 'warning', label: 'Partly checked' }
              : approvalConfigWarnings.length > 0
                ? { tone: 'warning', label: 'Needs attention' }
                : activeCount === 0
                  ? { tone: 'neutral', label: 'No employees' }
                  : { tone: 'success', label: 'Healthy' };

  const metaParts: React.ReactNode[] = isLoading
    ? ['Checking active employee records…']
    : employeesError
      ? ['Employee records didn’t load']
      : [
          flagged.length > 0
            ? `${formatNumber(flagged.length)} of ${formatCount(activeCount, 'active employee')} ${flagged.length === 1 ? 'needs' : 'need'} setup`
            : formatCount(activeCount, 'active employee'),
          isRefreshing ? 'Re-checking…' : checkedAt ? `Checked ${formatCheckedAt(checkedAt)}` : null,
        ];

  // The Broken / Partial tiles are shortcuts to the By employee severity filter.
  const severityOn = (severity: 'critical' | 'warning') => view === 'employee' && filter === severity;
  const toggleSeverity = (severity: 'critical' | 'warning') => {
    if (severityOn(severity)) {
      setFilter('all');
      return;
    }
    setView('employee');
    setFilter(severity);
  };
  const severityTile = (severity: 'critical' | 'warning', label: string, count: number, caption: string) => {
    const interactive = !isLoading && count > 0;
    const selected = interactive && severityOn(severity);
    return (
      <KpiTile
        size="md"
        loading={isLoading}
        label={label}
        value={formatNumber(count)}
        caption={caption}
        onClick={interactive ? () => toggleSeverity(severity) : undefined}
        selected={interactive ? selected : undefined}
        aria-label={
          interactive
            ? `${label}: ${formatCount(count, 'employee')}. ${selected ? 'Showing only these in By employee; select to show everyone.' : 'Select to show only these in By employee.'}`
            : undefined
        }
      />
    );
  };

  const shownCount = view === 'employee' ? employeeRows.length : matching.length;
  const nothingShown = view === 'employee' ? employeeRows.length === 0 : issueGroups.length === 0;

  let issues: React.ReactNode;
  if (isLoading) {
    issues = <IssuesSkeleton />;
  } else if (employeesError) {
    issues = (
      <Panel>
        <ErrorState
          title="Couldn’t load employees"
          body="The setup check didn’t run, so nothing here reflects your records yet. Try again in a minute."
          onRetry={() => refetchEmployees()}
          retryLabel="Retry check"
        />
      </Panel>
    );
  } else if (activeCount === 0) {
    issues = (
      <Panel>
        <EmptyState
          icon={<Users />}
          title="No active employees yet"
          body="Setup checks run on every active employee record. Add employees to see their setup here."
          action={
            <Button component={RouterLink} to="/employees" variant="outlined">
              View employees
            </Button>
          }
        />
      </Panel>
    );
  } else if (flagged.length === 0) {
    issues = auditIncomplete ? (
      // No issues found, but some checks never ran — do not claim the
      // all-clear the full audit would.
      <Panel>
        <EmptyState
          icon={<CircleDashed />}
          title="No issues found, but some checks didn’t run"
          body={`${skippedReasons.join(' ')} Retry to confirm every record is set up.`}
          action={<CheckAgainButton label="Retry checks" busy={checksFetching} onClick={retryChecks} />}
        />
      </Panel>
    ) : (
      <Panel>
        <EmptyState
          icon={<CircleCheck />}
          title={
            activeCount === 1
              ? 'Your 1 active employee is fully configured'
              : `All ${formatNumber(activeCount)} active employees are fully configured`
          }
          body="Every record has a linked manager, login, department and time-off allotments. New issues appear here as records change."
        />
      </Panel>
    );
  } else {
    issues = (
      <>
        {/* Where the side column stacks, the Checks filter sits here, above
            the list it filters, instead of after it. */}
        <Box sx={{ display: { xs: 'block', xl: 'none' } }}>
          <CheckAreaChips health={health} selected={area} onSelect={setArea} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <SegmentedControl size="small" aria-label="Group issues" value={view} onChange={setView} options={VIEW_OPTIONS} />
          {view === 'employee' && (
            <>
              <FilterChip selected={filter === 'critical'} onClick={() => setFilter(filter === 'critical' ? 'all' : 'critical')} count={criticalCount}>
                Broken
              </FilterChip>
              <FilterChip selected={filter === 'warning'} onClick={() => setFilter(filter === 'warning' ? 'all' : 'warning')} count={warningCount}>
                Partial
              </FilterChip>
            </>
          )}
          {area && (
            <FilterChip selected onClick={() => setArea(null)} sx={{ display: { xs: 'none', xl: 'inline-flex' } }}>
              {areaLabel(area)}
            </FilterChip>
          )}
          {filtersActive && (
            <Button variant="text" size="small" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
          <SearchField
            label="Search employees"
            placeholder="Search name, email, team or manager"
            value={search}
            onChange={setSearch}
            width={280}
            sx={{ ml: { sm: 'auto' } }}
          />
        </Box>
        {filtersActive && (
          <Typography role="status" variant="body2" sx={{ color: sbg.text.secondary, fontVariantNumeric: 'tabular-nums' }}>
            Showing {formatNumber(shownCount)} of {formatCount(flagged.length, 'employee')} with issues
          </Typography>
        )}
        {nothingShown ? (
          <Panel>
            <EmptyState
              compact
              title="No employees match these filters"
              body={
                query
                  ? `No one with issues matches “${query}”${area ? ` in ${areaLabel(area)}` : ''}.`
                  : 'No one with issues matches the selected filters.'
              }
              action={
                <Button variant="outlined" onClick={clearFilters}>
                  Clear filters
                </Button>
              }
            />
          </Panel>
        ) : view === 'issue' ? (
          <IssueGroups groups={issueGroups} actions={fixActions} />
        ) : (
          <EmployeeTable rows={employeeRows} onEdit={handleFix} />
        )}
      </>
    );
  }

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <Box
        component="header"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h3" component="h2" sx={{ color: sbg.text.primary }}>
              Setup health
            </Typography>
            {overall && <StatusPill tone={overall.tone}>{overall.label}</StatusPill>}
          </Box>
          <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary, mt: 0.5, maxWidth: '72ch' }}>
            Finds active employee records with missing links or fields that stop time off, approvals or notifications
            from reaching the right person.
          </Typography>
          <MetaLine parts={metaParts} />
        </Box>
        <Tooltip
          title={
            syncFixes
              ? 'Fixes the issues marked “Sync manager links” by linking records to their manager and login accounts.'
              : 'Links employee records to their manager and login accounts by matching names and emails.'
          }
        >
          <Box component="span" sx={{ flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'auto' } }}>
            {/* The one loud action when sync is the next step for a flagged issue. */}
            <SyncButton onSync={handleSync} isSyncing={isSyncing} size="medium" variant={syncFixes ? 'contained' : 'outlined'} />
          </Box>
        </Tooltip>
      </Box>

      {syncMessage && (
        <Alert
          severity={syncMessage.severity}
          role={syncMessage.severity === 'error' ? 'alert' : 'status'}
          onClose={() => setSyncMessage(null)}
        >
          <AlertTitle>{syncMessage.title}</AlertTitle>
          {syncMessage.text}
        </Alert>
      )}

      {approvalConfigProblems.length > 0 && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={showApprovalCard}>
              View details
            </Button>
          }
        >
          Sales WFH approvals aren’t fully configured. An engineer needs to change the server settings listed in Sales WFH
          approvals.
        </Alert>
      )}

      {!isLoading && !employeesError && auditIncomplete && flagged.length > 0 && (
        <Alert
          severity="warning"
          action={<CheckAgainButton color="inherit" variant="text" label="Retry checks" busy={checksFetching} onClick={retryChecks} />}
        >
          <AlertTitle>Some checks didn’t run</AlertTitle>
          {skippedReasons.join(' ')} The list below shows only what the remaining checks found.
        </Alert>
      )}

      {/* Counts are hidden when the employee list failed to load: zeroed or
          partial counts would read as a clean audit when nothing (or only
          part) was evaluated. The active total is in the meta line above. */}
      {!employeesError && (
        <KpiGrid columns={3} sx={{ mb: 0 }}>
          {severityTile('critical', 'Broken setup', criticalCount, 'Stops time off or emails')}
          {severityTile('warning', 'Partial setup', warningCount, 'Routing works, but fragile')}
          <KpiTile
            size="md"
            loading={isLoading}
            label="Fully configured"
            value={auditIncomplete ? MISSING : formatNumber(healthyCount)}
            caption={
              auditIncomplete
                ? 'Unknown until every check runs'
                : activeCount > 0
                  ? `${floorPercent(healthyCount, activeCount)} of active employees`
                  : 'No active employees'
            }
          />
        </KpiGrid>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          // Two columns only from xl: at lg the main column would be ~370px,
          // too narrow for the issue rows and the employee table.
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'minmax(0, 1fr) 340px' },
          alignItems: 'start',
        }}
      >
        <Box component="section" aria-label="Setup issues" sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
          {issues}
        </Box>
        <Box
          component="aside"
          aria-label="Checks and approval routing"
          sx={{
            display: 'grid',
            gap: 3,
            minWidth: 0,
            alignItems: 'start',
            order: { xs: configNeedsAttention ? -1 : 0, xl: 0 },
          }}
        >
          {!employeesError && (isLoading || activeCount !== 0) && (
            <Box sx={{ display: { xs: 'none', xl: 'block' }, minWidth: 0 }}>
              <ChecksCard health={health} coverage={coverage} selected={area} onSelect={setArea} loading={isLoading} />
            </Box>
          )}
          <ApprovalRoutingCard
            config={approvalConfig}
            loading={approvalConfigLoading}
            fetching={approvalConfigFetching && !approvalConfigLoading}
            error={approvalConfigError}
            problems={approvalConfigProblems}
            warnings={approvalConfigWarnings}
            onRetry={() => refetchApprovalConfig()}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SetupHealth;
